import React, { useEffect, useState } from 'react';
import { Flex, InputV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { Close, Search } from '@withjoy/joykit/icons';
import { useManagedSearchParam } from '@shared/hooks/useManagedSearchParam';
import { useSearchContext } from './SearchContext';
import { useRotatingText } from '@shared/hooks/useRotatingText';

const V2_SEARCH_QUERY_PARAM = 'q';

export const SearchBox: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { tq } = useTranslation('stationery');
  const [initialQ] = useManagedSearchParam(V2_SEARCH_QUERY_PARAM);
  const [term, setTerm] = useState(initialQ ?? '');
  const { setTerm: setSearchContextTerm } = useSearchContext();
  const placeholderExample = useRotatingText([tq`Beach`, tq`Floral`, tq`Green`]);

  useEffect(() => {
    setSearchContextTerm(term);
  }, [term, setSearchContextTerm]);

  return (
    <InputV2
      type="text"
      placeholder={`${tq`Search for`} ${placeholderExample}`}
      autoComplete="off"
      value={term}
      onChange={event => setTerm(event.target.value)}
      onKeyDown={event => {
        if (event.key === 'Escape') {
          setTerm('');
        }
      }}
      overrides={{
        Root: {
          props: {
            borderRadius: 24,
            width: 336, // Borrowed from website designer theme search box
            _after: { borderRadius: 24 },
            _focusWithin: {
              borderColor: 'mono14',
            },
            // Match the filter-pill / preview-button height (48px) in the gated filter-bar design.
            ...(compact ? { height: pxToRem(48) } : {}),
          },
        },
        ...(compact ? { Input: { props: { height: '100%', paddingY: 0 } } } : {}),
      }}
      startElement={<Search size={24} />}
      endElement={
        <Flex
          onClick={() => setTerm('')}
          role="button"
          aria-label={tq`Clear search`}
          tabIndex={term ? 0 : -1}
          {...(term
            ? {
                cursor: 'pointer',
                visibility: 'visible',
                pointerEvents: 'auto',
              }
            : {
                cursor: 'auto',
                visibility: 'hidden',
                pointerEvents: 'none',
              })}
          paddingX={1}
        >
          <Close size={24} />
        </Flex>
      }
    />
  );
};
